<template>
  <tbody>
    <AddNewContact
      v-if="!itemsActive"
      :existing-groups="inscopeData"
      @saveNewContact="onSaveNewContact"
    />
    <tr
      v-for="(item, i) in inscopeData"
      :key="i"
      :class="{ 'expanded-row': expandedItems[i] && hasMultipleContacts(item) }"
    >
      <td class="holder-checkbox">
        <CustomCheckbox
          :id="`checkbox_${i+1}`"
          :label-text="`checkbox_${i+1}`"
          :input-element="item"
        />
      </td>
      <td>
        <div class="holder-info">
          <div class="info">
            <div v-if="editMode === `${i}-name-0`" class="editable-cell-container">
              <CustomInput
                ref="editInput"
                :model-value="item.name"
                style="max-width: 160px;"
                @update:modelValue="updateField(i, 'name', 0, $event)"
                @keyup:enter="saveFieldData(item)"
                @keyup:esc="finishEditing"
              />
            </div>
            <span
              v-else
              class="secondary-black-color info-name"
              @click="startEditing(i, 'name', 0)"
            >
              {{ item.name }}
            </span>
          </div>
        </div>
      </td>
      <td>
        <div class="holder-info">
          <div class="info" :style="{ gap: expandedItems[i] ? '15px' : '' }">
            <div
              v-if="editMode === `${i}-countryCode-0` && item.contacts && item.contacts.length > 0"
              class="editable-cell-container"
            >
              <CustomInput
                ref="editInput"
                :model-value="item.contacts[0].countryCode"
                style="max-width: 160px;"
                input-type="digitsAndPlus"
                @update:modelValue="updateField(i, 'countryCode', 0, $event)"
                @keyup:enter="saveFieldData(item)"
                @keyup:esc="finishEditing"
              />
            </div>
            <span
              v-else-if="item.contacts && item.contacts.length > 0"
              class="secondary-black-color"
              @click="startEditing(i, 'countryCode', 0)"
            >
              {{ item.contacts[0].countryCode || '---' }}
            </span>

            <div v-if="hasMultipleContacts(item)" class="additional-contacts" :class="{ 'expanded': expandedItems[i] }">
              <div
                v-for="(contact, index) in item.contacts.slice(1)"
                :key="index"
              >
                <div v-if="editMode === `${i}-countryCode-${index+1}`" class="editable-cell-container">
                  <CustomInput
                    ref="editInput"
                    :model-value="contact.countryCode"
                    style="max-width: 160px;"
                    input-type="digitsAndPlus"
                    @update:modelValue="updateField(i, 'countryCode', index+1, $event)"
                    @keyup:enter="saveFieldData(item)"
                    @keyup:esc="finishEditing"
                  />
                </div>
                <span
                  v-else
                  class="secondary-black-color"
                  @click="startEditing(i, 'countryCode', index+1)"
                >
                  {{ contact.countryCode || '---' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </td>
      <td>
        <div class="holder-info">
          <div class="info" :style="{ gap: expandedItems[i] ? '15px' : '' }">
            <div
              v-if="editMode === `${i}-phoneNumber-0` && item.contacts && item.contacts.length > 0"
              class="editable-cell-container"
            >
              <CustomInput
                ref="editInput"
                :model-value="item.contacts[0].phoneNumber"
                style="max-width: 160px;"
                input-type="digits"
                @update:modelValue="updateField(i, 'phoneNumber', 0, $event)"
                @keyup:enter="saveFieldData(item)"
                @keyup:esc="finishEditing"
              />
            </div>
            <span
              v-else-if="item.contacts && item.contacts.length > 0"
              class="secondary-black-color"
              @click="startEditing(i, 'phoneNumber', 0)"
            >
              {{ item.contacts[0].phoneNumber || '---' }}
              <svg
                v-if="item.contacts[0].phoneNumber"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                @click.stop="copyPhoneNumber(item.contacts[0].phoneNumber)"
              >
                <path
                  d="M13.1875 0.47583H4.1875C4.03832 0.47583 3.89524 0.535093 3.78975 0.640583C3.68426 0.746072 3.625 0.889146 3.625 1.03833V3.85083H0.8125C0.663316 3.85083 0.520242 3.91009 0.414753 4.01558C0.309263 4.12107 0.25 4.26415 0.25 4.41333V13.4133C0.25 13.5625 0.309263 13.7056 0.414753 13.8111C0.520242 13.9166 0.663316 13.9758 0.8125 13.9758H9.8125C9.96168 13.9758 10.1048 13.9166 10.2102 13.8111C10.3157 13.7056 10.375 13.5625 10.375 13.4133V10.6008H13.1875C13.3367 10.6008 13.4798 10.5416 13.5852 10.4361C13.6907 10.3306 13.75 10.1875 13.75 10.0383V1.03833C13.75 0.889146 13.6907 0.746072 13.5852 0.640583C13.4798 0.535093 13.3367 0.47583 13.1875 0.47583ZM9.25 12.8508H1.375V4.97583H9.25V12.8508ZM12.625 9.47583H10.375V4.41333C10.375 4.26415 10.3157 4.12107 10.2102 4.01558C10.1048 3.91009 9.96168 3.85083 9.8125 3.85083H4.75V1.60083H12.625V9.47583Z"
                  fill="#696F79"
                />
              </svg>
              <transition name="fade">
                <span
                  v-if="(showTooltip === item.contacts[0].phoneNumber) && item.contacts[0].phoneNumber"
                  class="tooltip"
                  style="transform: translate(0%, -100%)"
                >
                  Copied
                </span>
              </transition>
            </span>

            <div v-if="hasMultipleContacts(item)" class="additional-contacts" :class="{ 'expanded': expandedItems[i] }">
              <div
                v-for="(contact, index) in item.contacts.slice(1)"
                :key="index"
              >
                <div v-if="editMode === `${i}-phoneNumber-${index+1}`" class="editable-cell-container">
                  <CustomInput
                    ref="editInput"
                    :model-value="contact.phoneNumber"
                    style="max-width: 160px;"
                    input-type="digits"
                    @update:modelValue="updateField(i, 'phoneNumber', index+1, $event)"
                    @keyup:enter="saveFieldData(item)"
                    @keyup:esc="finishEditing"
                  />
                </div>
                <span
                  v-else
                  class="secondary-black-color"
                  @click="startEditing(i, 'phoneNumber', index+1)"
                >
                  {{ contact.phoneNumber || '---' }}
                  <svg
                    v-if="contact.phoneNumber"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    @click.stop="copyPhoneNumber(contact.phoneNumber)"
                  >
                    <path
                      d="M13.1875 0.47583H4.1875C4.03832 0.47583 3.89524 0.535093 3.78975 0.640583C3.68426 0.746072 3.625 0.889146 3.625 1.03833V3.85083H0.8125C0.663316 3.85083 0.520242 3.91009 0.414753 4.01558C0.309263 4.12107 0.25 4.26415 0.25 4.41333V13.4133C0.25 13.5625 0.309263 13.7056 0.414753 13.8111C0.520242 13.9166 0.663316 13.9758 0.8125 13.9758H9.8125C9.96168 13.9758 10.1048 13.9166 10.2102 13.8111C10.3157 13.7056 10.375 13.5625 10.375 13.4133V10.6008H13.1875C13.3367 10.6008 13.4798 10.5416 13.5852 10.4361C13.6907 10.3306 13.75 10.1875 13.75 10.0383V1.03833C13.75 0.889146 13.6907 0.746072 13.5852 0.640583C13.4798 0.535093 13.3367 0.47583 13.1875 0.47583ZM9.25 12.8508H1.375V4.97583H9.25V12.8508ZM12.625 9.47583H10.375V4.41333C10.375 4.26415 10.3157 4.12107 10.2102 4.01558C10.1048 3.91009 9.96168 3.85083 9.8125 3.85083H4.75V1.60083H12.625V9.47583Z"
                      fill="#696F79"
                    />
                  </svg>
                  <transition name="fade">
                    <span
                      v-if="(showTooltip === contact.phoneNumber) && contact.phoneNumber"
                      class="tooltip"
                      style="transform: translate(0%, -100%)"
                    >Copied</span>
                  </transition>
                </span>
              </div>
            </div>
          </div>
        </div>
      </td>
      <td>
        <div class="holder-info">
          <div class="info" :style="{ gap: expandedItems[i] ? '15px' : '' }">
            <div
              v-if="editMode === `${i}-email-0` && item.contacts && item.contacts.length > 0"
              class="editable-cell-container"
            >
              <CustomInput
                ref="editInput"
                :model-value="item.contacts[0].email"
                style="max-width: 160px;"
                @update:modelValue="updateField(i, 'email', 0, $event)"
                @keyup:enter="saveFieldData(item)"
                @keyup:esc="finishEditing"
              />
            </div>
            <span
              v-else-if="item.contacts && item.contacts.length > 0"
              class="secondary-black-color"
              @click="startEditing(i, 'email', 0)"
            >
              {{ item.contacts[0].email || '---' }}
            </span>

            <div v-if="hasMultipleContacts(item)" class="additional-contacts" :class="{ 'expanded': expandedItems[i] }">
              <div
                v-for="(contact, index) in item.contacts.slice(1)"
                :key="index"
              >
                <div v-if="editMode === `${i}-email-${index+1}`" class="editable-cell-container">
                  <CustomInput
                    ref="editInput"
                    :model-value="contact.email"
                    style="max-width: 160px;"
                    @update:modelValue="updateField(i, 'email', index+1, $event)"
                    @keyup:enter="saveFieldData(item)"
                    @keyup:esc="finishEditing"
                  />
                </div>
                <span
                  v-else
                  class="secondary-black-color"
                  @click="startEditing(i, 'email', index+1)"
                >
                  {{ contact.email || '---' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </td>
      <td>
        <div class="holder-info">
          <div class="info" :style="{ gap: expandedItems[i] ? '15px' : '' }">
            <div
              v-if="editMode === `${i}-note-0` && item.contacts && item.contacts.length > 0"
              class="editable-cell-container"
            >
              <CustomInput
                ref="editInput"
                :model-value="item.contacts[0].note"
                style="max-width: 160px;"
                @update:modelValue="updateField(i, 'note', 0, $event)"
                @keyup:enter="saveFieldData(item)"
                @keyup:esc="finishEditing"
              />
            </div>
            <div v-else-if="item.contacts && item.contacts.length > 0" class="note-container">
              <span
                :ref="`note-${i}-0`"
                class="secondary-black-color"
                :class="{ 'expanded': isNoteExpanded(i, 0) }"
                @click="startEditing(i, 'note', 0)"
              >
                {{ item.contacts[0].note || '---' }}
              </span>
              <button
                v-if="isNoteTruncated(i, 0) && !isNoteExpanded(i, 0)"
                class="view-all-button"
                @click.stop="toggleNoteExpansion(i, 0)"
              >
                View all
              </button>
              <button
                v-if="isNoteExpanded(i, 0)"
                class="view-all-button collapse-button"
                @click.stop="toggleNoteExpansion(i, 0)"
              >
                Collapse
              </button>
            </div>

            <div v-if="hasMultipleContacts(item)" class="additional-contacts" :class="{ 'expanded': expandedItems[i] }">
              <div
                v-for="(contact, index) in item.contacts.slice(1)"
                :key="index"
              >
                <div v-if="editMode === `${i}-note-${index+1}`" class="editable-cell-container">
                  <CustomInput
                    ref="editInput"
                    :model-value="contact.note"
                    style="max-width: 160px;"
                    @update:modelValue="updateField(i, 'note', index+1, $event)"
                    @keyup:enter="saveFieldData(item)"
                    @keyup:esc="finishEditing"
                  />
                </div>
                <div v-else class="note-container">
                  <span
                    :ref="`note-${i}-${index+1}`"
                    class="secondary-black-color"
                    :class="{ 'expanded': isNoteExpanded(i, index+1) }"
                    @click="startEditing(i, 'note', index+1)"
                  >
                    {{ contact.note || '---' }}
                  </span>
                  <button
                    v-if="isNoteTruncated(i, index+1) && !isNoteExpanded(i, index+1)"
                    class="view-all-button"
                    @click.stop="toggleNoteExpansion(i, index+1)"
                  >
                    View all
                  </button>
                  <button
                    v-if="isNoteExpanded(i, index+1)"
                    class="view-all-button collapse-button"
                    @click.stop="toggleNoteExpansion(i, index+1)"
                  >
                    Collapse
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </td>
      <td v-if="item.contacts.length > 0" class="action-buttons-cell">
        <div class="info" :style="{ gap: expandedItems[i] ? '15px' : '' }">
          <div class="action-buttons">
            <button class="btn-action btn-copy-data" @click="copyContactData(item, i, 0)">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20.25 3H8.25C8.05109 3 7.86032 3.07902 7.71967 3.21967C7.57902 3.36032 7.5 3.55109 7.5 3.75V7.5H3.75C3.55109 7.5 3.36032 7.57902 3.21967 7.71967C3.07902 7.86032 3 8.05109 3 8.25V20.25C3 20.4489 3.07902 20.6397 3.21967 20.7803C3.36032 20.921 3.55109 21 3.75 21H15.75C15.9489 21 16.1397 20.921 16.2803 20.7803C16.421 20.6397 16.5 20.4489 16.5 20.25V16.5H20.25C20.4489 16.5 20.6397 16.421 20.7803 16.2803C20.921 16.1397 21 15.9489 21 15.75V3.75C21 3.55109 20.921 3.36032 20.7803 3.21967C20.6397 3.07902 20.4489 3 20.25 3ZM15 19.5H4.5V9H15V19.5ZM19.5 15H16.5V8.25C16.5 8.05109 16.421 7.86032 16.2803 7.71967C16.1397 7.57902 15.9489 7.5 15.75 7.5H9V4.5H19.5V15Z"
                  fill="#696F79"
                />
              </svg>
              <transition name="fade">
                <span
                  v-if="showTooltip === `copied-${i}-0`"
                  class="tooltip"
                  style="transform: translate(-30%, -100%)"
                >Copied</span>
              </transition>
            </button>
            <button class="btn-action btn-delete" @click="deleteContact(item, i, 0)">
              <svg
                width="23"
                height="24"
                viewBox="0 0 23 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18.375 5.96154H4.625C4.45924 5.96154 4.30027 6.02637 4.18306 6.14178C4.06585 6.25719 4 6.41371 4 6.57692C4 6.74013 4.06585 6.89666 4.18306 7.01207C4.30027 7.12747 4.45924 7.19231 4.625 7.19231H5.25V18.2692C5.25 18.5957 5.3817 18.9087 5.61612 19.1395C5.85054 19.3703 6.16848 19.5 6.5 19.5H16.5C16.8315 19.5 17.1495 19.3703 17.3839 19.1395C17.6183 18.9087 17.75 18.5957 17.75 18.2692V7.19231H18.375C18.5408 7.19231 18.6997 7.12747 18.8169 7.01207C18.9342 6.89666 19 6.74013 19 6.57692C19 6.41371 18.9342 6.25719 18.8169 6.14178C18.6997 6.02637 18.5408 5.96154 18.375 5.96154ZM16.5 18.2692H6.5V7.19231H16.5V18.2692ZM7.75 4.11538C7.75 3.95217 7.81585 3.79565 7.93306 3.68024C8.05027 3.56483 8.20924 3.5 8.375 3.5H14.625C14.7908 3.5 14.9497 3.56483 15.0669 3.68024C15.1842 3.79565 15.25 3.95217 15.25 4.11538C15.25 4.27859 15.1842 4.43512 15.0669 4.55053C14.9497 4.66593 14.7908 4.73077 14.625 4.73077H8.375C8.20924 4.73077 8.05027 4.66593 7.93306 4.55053C7.81585 4.43512 7.75 4.27859 7.75 4.11538Z"
                  fill="#F1105F"
                />
              </svg>
            </button>
          </div>

          <div v-if="hasMultipleContacts(item)" class="additional-contacts" :class="{ 'expanded': expandedItems[i] }">
            <div
              v-for="(contact, index) in item.contacts.slice(1)"
              :key="index"
              class="action-buttons"
            >
              <button class="btn-action btn-copy-data" @click="copyContactData(item, i, index+1)">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20.25 3H8.25C8.05109 3 7.86032 3.07902 7.71967 3.21967C7.57902 3.36032 7.5 3.55109 7.5 3.75V7.5H3.75C3.55109 7.5 3.36032 7.57902 3.21967 7.71967C3.07902 7.86032 3 8.05109 3 8.25V20.25C3 20.4489 3.07902 20.6397 3.21967 20.7803C3.36032 20.921 3.55109 21 3.75 21H15.75C15.9489 21 16.1397 20.921 16.2803 20.7803C16.421 20.6397 16.5 20.4489 16.5 20.25V16.5H20.25C20.4489 16.5 20.6397 16.421 20.7803 16.2803C20.921 16.1397 21 15.9489 21 15.75V3.75C21 3.55109 20.921 3.36032 20.7803 3.21967C20.6397 3.07902 20.4489 3 20.25 3ZM15 19.5H4.5V9H15V19.5ZM19.5 15H16.5V8.25C16.5 8.05109 16.421 7.86032 16.2803 7.71967C16.1397 7.57902 15.9489 7.5 15.75 7.5H9V4.5H19.5V15Z"
                    fill="#696F79"
                  />
                </svg>
                <transition name="fade">
                  <span
                    v-if="showTooltip === `copied-${i}-${index+1}`"
                    class="tooltip"
                    style="transform: translate(-30%, -100%)"
                  >Copied</span>
                </transition>
              </button>
              <button class="btn-action btn-delete" @click="deleteContact(item, i, index+1)">
                <svg
                  width="23"
                  height="24"
                  viewBox="0 0 23 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.375 5.96154H4.625C4.45924 5.96154 4.30027 6.02637 4.18306 6.14178C4.06585 6.25719 4 6.41371 4 6.57692C4 6.74013 4.06585 6.89666 4.18306 7.01207C4.30027 7.12747 4.45924 7.19231 4.625 7.19231H5.25V18.2692C5.25 18.5957 5.3817 18.9087 5.61612 19.1395C5.85054 19.3703 6.16848 19.5 6.5 19.5H16.5C16.8315 19.5 17.1495 19.3703 17.3839 19.1395C17.6183 18.9087 17.75 18.5957 17.75 18.2692V7.19231H18.375C18.5408 7.19231 18.6997 7.12747 18.8169 7.01207C18.9342 6.89666 19 6.74013 19 6.57692C19 6.41371 18.9342 6.25719 18.8169 6.14178C18.6997 6.02637 18.5408 5.96154 18.375 5.96154ZM16.5 18.2692H6.5V7.19231H16.5V18.2692ZM7.75 4.11538C7.75 3.95217 7.81585 3.79565 7.93306 3.68024C8.05027 3.56483 8.20924 3.5 8.375 3.5H14.625C14.7908 3.5 14.9497 3.56483 15.0669 3.68024C15.1842 3.79565 15.25 3.95217 15.25 4.11538C15.25 4.27859 15.1842 4.43512 15.0669 4.55053C14.9497 4.66593 14.7908 4.73077 14.625 4.73077H8.375C8.20924 4.73077 8.05027 4.66593 7.93306 4.55053C7.81585 4.43512 7.75 4.27859 7.75 4.11538Z"
                    fill="#F1105F"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </td>
      <td v-if="hasMultipleContacts(item)" class="toggle-cell">
        <button class="toggle-button" @click="toggleContacts(i)">
          <svg
            width="21"
            height="21"
            viewBox="0 0 21 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            :class="{ 'opened': expandedItems[i] }"
          >
            <path
              d="M16.8402 7.38572L11.3859 12.84C11.3352 12.8907 11.2751 12.9309 11.2089 12.9584C11.1427 12.9858 11.0717 13 11 13C10.9283 13 10.8573 12.9858 10.7911 12.9584C10.7249 12.9309 10.6648 12.8907 10.6141 12.84L5.15984 7.38572C5.0575 7.28338 5 7.14457 5 6.99983C5 6.8551 5.0575 6.71629 5.15984 6.61394C5.26218 6.5116 5.40099 6.4541 5.54573 6.4541C5.69047 6.4541 5.82928 6.5116 5.93162 6.61394L11 11.683L16.0684 6.61394C16.1191 6.56327 16.1792 6.52307 16.2454 6.49564C16.3116 6.46822 16.3826 6.4541 16.4543 6.4541C16.5259 6.4541 16.5969 6.46822 16.6631 6.49564C16.7293 6.52307 16.7895 6.56327 16.8402 6.61394C16.8908 6.66462 16.931 6.72478 16.9585 6.79099C16.9859 6.8572 17 6.92817 17 6.99983C17 7.0715 16.9859 7.14246 16.9585 7.20867C16.931 7.27488 16.8908 7.33505 16.8402 7.38572Z"
              fill="#696F79"
            />
          </svg>
        </button>
      </td>
      <td v-else />
    </tr>
  </tbody>
</template>

<script>
import CustomCheckbox from '@/components/inputs/CustomCheckbox.vue';
import CustomInput from '@/components/inputs/CustomInput.vue';
import AddNewInscopeContact from '@/components/cases/case-settings/AddNewInscopeContact.vue';
import debounce from '@/mixins/debounce';

export default {
  name: 'InscopeItem',
  components: {CustomCheckbox, CustomInput, AddNewContact: AddNewInscopeContact},
  emits: ['groupUpdated', 'saveNewContact', 'deleteContact'],
  mixins: [debounce],
  props: {
    inscopeData: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      showTooltip: null,
      expandedItems: {},
      expandedNotes: {}, // Format: "rowIndex-contactIndex"
      truncatedNotes: {}, // Format: "rowIndex-contactIndex"
      editMode: null, // Format: "rowIndex-fieldName-contactIndex"
    };
  },
  computed: {
    itemsActive() {
      return this.inscopeData.filter(item => item.isActive).length > 1;
    }
  },
  watch: {
    inscopeData: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.checkTruncatedNotes();
        });
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.checkTruncatedNotes();
    });

    window.addEventListener('resize', this.checkTruncatedNotes);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkTruncatedNotes);
  },
  methods: {
    onSaveNewContact(newContactData) {
      this.$emit('saveNewContact', newContactData);
    },

    copyPhoneNumber(phoneNumber) {
      navigator.clipboard.writeText(phoneNumber)
        .then(() => {
          this.showTooltip = phoneNumber;

          this.debounce(() => {
            this.showTooltip = null;
          }, 2000);
        });
    },
    toggleContacts(itemIndex) {
      this.$set(this.expandedItems, itemIndex, !this.expandedItems[itemIndex]);
    },
    hasMultipleContacts(item) {
      return item.contacts && item.contacts.length > 1;
    },
    startEditing(rowIndex, fieldName, contactIndex = 0) {
      this.finishEditing();
      this.editMode = `${rowIndex}-${fieldName}-${contactIndex}`;
      this.$nextTick(() => {
        if (this.$refs.editInput && this.$refs.editInput[0]) {
          this.$refs.editInput[0].$el.focus();
        }
      });
    },
    saveFieldData(item) {
      this.debounce(() => this.$emit('groupUpdated', item), 300);
      this.finishEditing();
    },
    finishEditing() {
      this.editMode = null;
    },
    updateField(rowIndex, fieldName, contactIndex, value) {
      const item = this.inscopeData[rowIndex];

      if (fieldName === 'countryCode') {
        value = value.replace(/[^0-9+]/g, '');
      } else if (fieldName === 'phoneNumber') {
        value = value.replace(/[^0-9]/g, '');
      }

      if (fieldName === 'name') {
        this.$set(item, 'name', value);
      } else if (item.contacts && item.contacts.length > contactIndex) {
        this.$set(item.contacts[contactIndex], fieldName, value);
      }
    },

    checkTruncatedNotes() {
      this.truncatedNotes = {};

      this.inscopeData.forEach((item, rowIndex) => {
        if (item.contacts && item.contacts.length > 0) {
          this.checkNoteTruncation(rowIndex, 0);

          if (item.contacts.length > 1) {
            for (let i = 1; i < item.contacts.length; i++) {
              this.checkNoteTruncation(rowIndex, i);
            }
          }
        }
      });
    },

    checkNoteTruncation(rowIndex, contactIndex) {
      const noteKey = `note-${rowIndex}-${contactIndex}`;
      const noteElement = this.$refs[noteKey];

      if (noteElement && noteElement[0]) {
        const isTruncated = noteElement[0].scrollWidth > noteElement[0].clientWidth;
        const key = `${rowIndex}-${contactIndex}`;
        this.$set(this.truncatedNotes, key, isTruncated);
      }
    },

    isNoteTruncated(rowIndex, contactIndex) {
      const key = `${rowIndex}-${contactIndex}`;
      return !!this.truncatedNotes[key];
    },

    toggleNoteExpansion(rowIndex, contactIndex) {
      const key = `${rowIndex}-${contactIndex}`;
      this.$set(this.expandedNotes, key, !this.expandedNotes[key]);
    },

    isNoteExpanded(rowIndex, contactIndex) {
      const key = `${rowIndex}-${contactIndex}`;
      return !!this.expandedNotes[key];
    },

    copyContactData(item, rowIndex, contactIndex = 0) {
      if (item.contacts && item.contacts.length > contactIndex) {
        const contact = item.contacts[contactIndex];
        const contactData = {
          name: item.name,
          countryCode: contact.countryCode || '',
          phoneNumber: contact.phoneNumber || '',
          email: contact.email || '',
          note: contact.note || ''
        };

        const textToCopy = `Group Name: ${contactData.name}\n`
          + `Country Code: ${contactData.countryCode}\n`
          + `Phone Number: ${contactData.phoneNumber}\n`
          + `Email: ${contactData.email}\n`
          + `Note: ${contactData.note}`;

        navigator.clipboard.writeText(textToCopy)
          .then(() => {
            this.showTooltip = `copied-${rowIndex}-${contactIndex}`;

            this.debounce(() => {
              this.showTooltip = null;
            }, 2000);
          });
      }
    },

    deleteContact(item, rowIndex, contactIndex = 0) {
      this.$emit('deleteContact', {item, index: contactIndex});
    }
  }
};
</script>

<style scoped>
td {
  vertical-align: top;
  box-shadow: 0 1px var(--border-color);
}

.holder-info .info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  height: 100%;
  overflow: visible;
  padding-top: 5px;
}

.holder-info .info span {
  font-weight: 400;
  font-size: 14px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 210px;
  width: 100%;
  color: var(--secondary-black-color);
  cursor: pointer;
}

.holder-info .info span:hover {
  text-decoration: underline;
}

.holder-info .info span.info-name {
  background-color: white;
  width: 100%;
  display: block;
}

.secondary-black-color {
  display: flex;
  align-items: center;
  gap: 5px;
  min-height: 22px;
}

.secondary-black-color svg {
  opacity: 0;
  cursor: pointer;
  transition: opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.secondary-black-color:hover svg {
  opacity: 1;
}

.note-container span.expanded svg {
  position: absolute;
  right: 5px;
  top: 0;
  opacity: 1;
  z-index: 2;
}

.tooltip {
  position: absolute;
  z-index: 10;
  pointer-events: none;
  background-color: rgba(0, 0, 0, 0.75);
  color: #fff !important;
  padding: 5px 0;
  border-radius: 4px;
  font-size: 12px;
  text-align: center;
  transition: opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1), transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.additional-contacts {
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  width: 100%;
  transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
  opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1),
  margin 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 0;
}

.additional-contacts.expanded {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  max-height: fit-content;
  opacity: 1;
  padding-right: 5px;
  transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1),
  opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1),
  margin 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Toggle button styles */
.toggle-cell {
  vertical-align: top;
  text-align: center;
  position: relative;
  height: 100%;
  overflow: visible;
  padding: 16px 0 0;
}

.toggle-button {
  border: none;
  padding: 0;
  font-size: 12px;
  cursor: pointer;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  color: var(--secondary-black-color);
  display: flex;
  align-items: center;
  justify-content: center;
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
  width: 100%;
}

.toggle-button svg {
  min-width: 21px;
  min-height: 21px;
  flex-shrink: 0;
}

/* Fade transition for tooltip */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.opened {
  transform: rotate(180deg);
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.editable-cell-container {
  width: 100%;
}

.editable-cell-container input {
  width: 100%;
  border-radius: 4px;
  border: 1px solid var(--input-placeholder-value-color);
  padding: 6px 10px;
  font-size: 14px;
  color: var(--secondary-black-color);
}

.editable-cell-container input:focus {
  outline: none;
  border-color: var(--border-color);
  box-shadow: 0 0 3px rgba(0, 123, 255, 0.25);
}

/* Note container and View all button styles */
.note-container {
  position: relative;
  width: 100%;
  max-width: 190px;
}

.note-container span.expanded::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 20px;
  pointer-events: none;
  opacity: 1;
}

.note-container span {
  display: block;
}

.note-container span.expanded {
  white-space: normal;
  word-break: break-word;
  overflow-y: auto;
  text-overflow: initial;
  max-height: 200px;
  scrollbar-width: thin;
  scrollbar-color: var(--border-color) transparent;
  display: block;
  height: auto;
  align-items: flex-start;
}

.note-container span.expanded::-webkit-scrollbar {
  width: 4px;
}

.note-container span.expanded::-webkit-scrollbar-track {
  background: transparent;
}

.note-container span.expanded::-webkit-scrollbar-thumb {
  background-color: var(--border-color);
  border-radius: 4px;
}

.view-all-button {
  position: absolute;
  right: 0;
  top: 0;
  color: var(--accent-color);
  font-size: 12px;
  cursor: pointer;
  padding: 1px 5px 5px 5px;
  margin-left: 5px;
  font-weight: 500;
  background-color: var(--white-color);
  opacity: 0;
  transition: all .5s;
  border: 1px solid var(--black-color);
}

.view-all-button:hover {
  text-decoration: underline;
}

.collapse-button {
  opacity: 0;
  right: 0;
  top: 0;
  background-color: var(--white-color);
  z-index: 2;
  border: 1px solid var(--black-color);
  transition: all .5s;
}

.note-container:hover .collapse-button,
.note-container:hover .view-all-button {
  opacity: 1;
}

/* Action buttons styles */
.action-buttons-cell {
  vertical-align: top;
  text-align: center;
  padding: 16px 0 0;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 5px;
}

.btn-action {
  display: flex;
  align-items: center;
  gap: 5px;
  border: none;
  background: none;
  font-size: 14px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.3s;
  white-space: nowrap;
}

.btn-action:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.btn-copy-data {
  color: var(--secondary-black-color);
}

.btn-delete {
  color: #F1105F;
}

.btn-action svg {
  max-width: 20px;
  max-height: 20px;
  flex-shrink: 0;
}

</style>
